/**
 * Cleovivra: show the header's secondary navigation bar only mid-page.
 *
 * Desired behaviour:
 *   - absent at rest, so the hero shows the single-row transparent header,
 *   - appears as soon as the page starts scrolling,
 *   - gone again from the third section onward, so it never overlaps the sticky
 *     collection-links scroll animation further down the page.
 *
 * The hide threshold is measured against the viewport top (0) rather than the
 * header's own height on purpose: the row is part of the header, so measuring
 * against header height would grow the threshold the moment the row appeared
 * and oscillate the state.
 */

const HEADER_SELECTOR = '#header-component';
const SECTION_SELECTOR = '[id^="shopify-section-template--"]';
/* Section index at which the row should disappear (0-based): the third. */
const HIDE_FROM_SECTION = 2;
/* Reveal essentially on the first scroll tick, but not while sitting at rest. */
const SHOW_AFTER_PX = 4;

function init() {
  const header = document.querySelector(HEADER_SELECTOR);
  if (!header) return;
  if (!header.querySelector('.header__navigation-bar-row')) return;

  const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
  const stopAt = sections[HIDE_FROM_SECTION] ?? null;
  const wrapper = document.querySelector('.page-wrapper');

  /*
   * Sections that render full-bleed imagery behind the header. A solid white
   * bar sitting on top of these looks pasted on, so the header reverts to its
   * transparent treatment whenever one of them is underneath it.
   */
  const mediaSections = sections.filter((s) => s.querySelector('.hero, collection-links-component'));

  let queued = false;
  let last = '';
  let lastMedia = '';

  const update = () => {
    queued = false;

    // Desktop scrolls .page-wrapper, mobile scrolls the document — take
    // whichever has actually moved rather than branching on viewport width.
    const scrolled = Math.max(window.scrollY || 0, wrapper?.scrollTop || 0);
    const stopTop = stopAt ? stopAt.getBoundingClientRect().top : Infinity;
    const state = scrolled > SHOW_AFTER_PX && stopTop > 0 ? 'visible' : 'hidden';

    // Probe the header's own mid-line: whatever sits there is what it overlaps.
    const probe = (header.getBoundingClientRect().height || 0) / 2;
    const overMedia = mediaSections.some((section) => {
      const rect = section.getBoundingClientRect();
      return rect.top <= probe && rect.bottom > probe;
    });
    const media = overMedia ? 'true' : 'false';

    if (media !== lastMedia) {
      lastMedia = media;
      header.dataset.cleoOverMedia = media;
    }

    if (state === last) return;
    last = state;
    header.dataset.cleoNavbar = state;
  };

  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };

  // Horizon scrolls div.page-wrapper on desktop but the document on mobile.
  document.querySelector('.page-wrapper')?.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  update();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

/* The theme editor re-renders sections in place; re-measure against new nodes. */
document.addEventListener('shopify:section:load', init);
